/**
 * The company is closed while it waits to be deleted, and this is the only
 * screen anybody in it can see.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT REPLACES THE APP, IT DOES NOT SIT ABOVE IT
 *
 * A banner over a working dashboard says "this is ending" while every button
 * still invites you to add another invoice to it. The server answers 423 to all
 * of them, so the honest screen is the whole screen: the workspace is shut, the
 * data is safe, here is the date, and here is the one way back.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * GETTING BACK IN COSTS WHAT CLOSING IT COST
 *
 * The company's own name, typed again. A locked company that reopens on one
 * click from anybody holding the owner's laptop is a company whose deletion
 * never really happened. The server checks the name too, so this is a
 * courtesy, not the enforcement.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * AND IT SAYS WHAT DOES NOT COME BACK
 *
 * The subscription was cancelled at Stripe the moment the company closed, and a
 * cancelled subscription cannot be un-cancelled. Everything else returns
 * untouched. Somebody discovering that on their own, a week later, when a write
 * is refused, would rightly call it a trick.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, LogOut } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import { Logo } from '@/components/ui/Logo';
import { useI18n } from '@/contexts/I18nContext';
import { useAuth } from '@/contexts/AuthContext';
import { api, ApiError } from '@/lib/api';

type OrgResponse = {
  organization: {
    name: string;
    name_ar: string | null;
    deletion?: { purge_at: string | null; days_left: number | null } | null;
  };
};

export function ClosedCompany() {
  const { t, dir, lang } = useI18n();
  const { user, org: orgSummary, logout, organizations, switchOrg } = useAuth();
  const qc = useQueryClient();
  const [typed, setTyped] = useState('');
  const [fault, setFault] = useState<string | null>(null);

  const { data } = useQuery<OrgResponse>({
    queryKey: ['org'],
    queryFn: () => api<OrgResponse>('/org'),
  });

  const org = data?.organization;
  const days = org?.deletion?.days_left ?? 0;
  const on = org?.deletion?.purge_at
    ? new Date(org.deletion.purge_at).toLocaleDateString(
        lang === 'ar' ? 'ar-BH-u-nu-latn' : 'en-GB',
        { day: 'numeric', month: 'long', year: 'numeric' },
      )
    : '';

  const matches =
    !!org &&
    (typed.trim().toLocaleLowerCase() === org.name.trim().toLocaleLowerCase() ||
      (!!org.name_ar && typed.trim().toLocaleLowerCase() === org.name_ar.trim().toLocaleLowerCase()));

  const restore = useMutation({
    mutationFn: () =>
      api('/org/deletion', { method: 'DELETE', body: { confirm_name: typed } }),
    onSuccess: async () => {
      await qc.invalidateQueries();
      /* A hard reload rather than a route change: every query in the app was
         answered 423 while the company was shut, and the cleanest way back to a
         working dashboard is to ask for all of them again from nothing. */
      window.location.assign('/app/dashboard');
    },
    onError: (e: ApiError) =>
      setFault(e.code === 'NAME_MISMATCH' ? t('org_delete_name_wrong') : e.message),
  });

  /*
    Somebody with a second company is not stuck here.

    Filtered on `org.id` from the auth context and NOT on `user.org`. The
    decoded token carries an `org` claim, so `user.org` is right for exactly as
    long as it takes `/auth/me` to answer: that response's `user` has no org on
    it, so after the first refresh the field is undefined and the filter removed
    nothing. The closed company then listed ITSELF under "you also belong to",
    inviting somebody to switch into the workspace they were being told was
    shut. `org` is the context's answer to "which company am I in" everywhere
    else, including the switcher, which is why the switcher's tick was right
    while this was wrong.
  */
  const here = orgSummary?.id ?? user?.org;
  const elsewhere = (organizations ?? []).filter((o) => o.id !== here);

  return (
    <div className="flex min-h-svh flex-col bg-background" dir={dir}>
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <Logo size={22} />
        <Button variant="ghost" size="sm" onClick={() => void logout()}>
          <LogOut className="size-4" />
          {t('logout')}
        </Button>
      </header>

      <main className="mx-auto flex w-full max-w-lg flex-1 flex-col justify-center px-6 sm:px-10">
        <span
          className="flex size-11 items-center justify-center rounded-xl bg-destructive/10 text-destructive"
          aria-hidden
        >
          <AlertTriangle className="size-5" />
        </span>

        <h1 className="mt-6 text-2xl font-semibold tracking-tight sm:text-3xl">
          {t('closed_title')}
        </h1>
        <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
          {t('closed_lede').replace('{n}', String(days)).replace('{date}', on)}
        </p>

        {user?.role === 'Owner' ? (
          <>
            <p className="mt-8 text-sm leading-relaxed">
              {t('closed_restore_lede').replace('{name}', org?.name ?? '')}
            </p>
            <input
              autoFocus
              dir="auto"
              value={typed}
              onChange={(e) => {
                setTyped(e.target.value);
                setFault(null);
              }}
              placeholder={org?.name ?? ''}
              aria-label={t('closed_restore_lede')}
              className="mt-3 w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />
            {fault ? <p className="mt-2 text-[13px] text-destructive">{fault}</p> : null}
            <Button
              className="mt-4 h-11 self-start"
              size="lg"
              disabled={!matches || restore.isPending}
              onClick={() => restore.mutate()}
            >
              {restore.isPending ? <Loader2 className="size-4 animate-spin" /> : null}
              {t('closed_restore_cta')}
            </Button>
            <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
              {t('closed_restore_note')}
            </p>
          </>
        ) : (
          <p className="mt-8 text-sm leading-relaxed text-muted-foreground">
            {t('closed_ask_owner')}
          </p>
        )}

        {elsewhere.length > 0 ? (
          <div className="mt-10 border-t border-border pt-6">
            <p className="text-[13px] text-muted-foreground">{t('closed_other_companies')}</p>
            <ul className="mt-3 grid gap-2">
              {elsewhere.map((o) => (
                <li key={o.id}>
                  <button
                    type="button"
                    onClick={() => void switchOrg(o.id)}
                    className="flex items-center gap-2 text-sm underline-offset-4 hover:underline"
                    dir="auto"
                  >
                    {o.name}
                    {/* A second company that is ALSO closing says so here for
                        the same reason the switcher does. Somebody who closed
                        two must not be sent from one shut door to another with
                        no warning. */}
                    {o.days_left !== null && o.days_left !== undefined ? (
                      <span className="rounded-full bg-destructive/10 px-2 py-0.5 text-[10.5px] font-medium text-destructive">
                        {t('org_switch_deleting').replace('{n}', String(o.days_left))}
                      </span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </main>
    </div>
  );
}
